import { Palette, DossierTemplate, VisualAesthetic } from '../types';

// Encoding helpers using TextEncoder/TextDecoder for Unicode safety and standard Base64.
// Canonical implementations live in services/firestoreUtils — re-exported here for app-level use.
export { safeEncode, safeDecode } from '../services/firestoreUtils';

// Firestore does not support 'undefined' values. This helper recursively removes them.
// Also strips base64 image data from image URL fields to prevent multi-MB documents.
// NOTE: this variant differs from hooks/useTripPersistence's sanitizeForFirestore —
// that one does not strip base64 image data. Moved verbatim from App.tsx.
export const sanitizeForFirestore = (obj: any, _visited = new WeakSet()): any => {
  if (Array.isArray(obj)) {
    return obj.filter(v => v !== undefined && v !== null).map(v => sanitizeForFirestore(v, _visited));
  } else if (obj instanceof Date) {
    return obj.toISOString();
  } else if (obj !== null && typeof obj === 'object') {
    // Preserve Firestore FieldValue sentinels (serverTimestamp, increment, etc.)
    if (obj.constructor?.name === 'FieldValue' || obj._methodName || obj.type === 'AggregateField') {
      return obj;
    }
    // Prevent infinite recursion on circular references
    if (_visited.has(obj)) return null;
    _visited.add(obj);
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== undefined && v !== null && typeof v !== 'function')
        .filter(([_, v]) => typeof v !== 'number' || (isFinite(v) && !isNaN(v)))
        .map(([k, v]) => {
          // Strip base64 image data from URL fields — these bloat Firestore docs to multi-MB
          if (typeof v === 'string' && v.startsWith('data:image/') && v.length > 1000) {
            return [k, ''];
          }
          return [k, sanitizeForFirestore(v, _visited)];
        })
    );
  }
  if (typeof obj === 'number' && (!isFinite(obj) || isNaN(obj))) return 0;
  return obj;
};

// CONTRAST CALIBRATION NOTES:
export const PALETTE_DEFINITIONS: Record<Palette, { ivory: string; charcoal: string; gold: string; sand: string }> = {
  [Palette.ObsidianNoir]: {
      ivory: '#EAEAEA',
      charcoal: '#0A0A0A',
      gold: '#D4AF37',
      sand: '#333333'
  },
  [Palette.VogueMinimal]: {
      ivory: '#FFFBF8',  // Warm ivory instead of pure white
      charcoal: '#000000',
      gold: '#A67C00', // Boosted gold for warmth + contrast on warm ivory
      sand: '#F0F0F0'
  },
  [Palette.MediterraneanSoul]: {
      ivory: '#F5F2EA',
      charcoal: '#1B2E25',
      gold: '#6B4E07', // Darker gold, passes WCAG AA 4.5:1 on cream
      sand: '#E3DDD3'
  },
  [Palette.GoldenHour]: {
      ivory: '#FFFCF2',
      charcoal: '#3E2723',
      gold: '#A67C00', // Darker gold for contrast on light yellow
      sand: '#F2E6D8'
  },
  [Palette.WatercolorDream]: {
      ivory: '#F8FAFC',
      charcoal: '#0F172A',
      gold: '#8B7A4A', // Muted warm gold, restores gold identity
      sand: '#E2E8F0'
  },
  [Palette.TheHotelCollection]: {
      ivory: '#F4F1EA',
      charcoal: '#2A2A2A',
      gold: '#6A5028', // Darker gold, passes WCAG AA 4.5:1
      sand: '#D3CFC2'
  }
};

export const TEMPLATE_PRESETS: Record<DossierTemplate, { palette: Palette; aesthetic: VisualAesthetic }> = {
  [DossierTemplate.TheMinimalist]: {
    palette: Palette.VogueMinimal,
    aesthetic: VisualAesthetic.EditorialVogue
  },
  [DossierTemplate.TheNoir]: {
    palette: Palette.ObsidianNoir,
    aesthetic: VisualAesthetic.HighEndPhotography
  },
  [DossierTemplate.TheArtist]: {
    palette: Palette.WatercolorDream,
    aesthetic: VisualAesthetic.HandPaintedWatercolor
  },
  [DossierTemplate.TheClassic]: {
    palette: Palette.TheHotelCollection,
    aesthetic: VisualAesthetic.TheHotelCollection
  },
  [DossierTemplate.TheAdventurer]: {
    palette: Palette.MediterraneanSoul,
    aesthetic: VisualAesthetic.HighEndPhotography
  }
};
